import { useMemo, useState } from 'react';
import { Store, Warehouse } from 'lucide-react';
import { useProductStockMovements, useQuotedPriceHistory } from '@/hooks/use-product-intelligence';
import {
  INITIAL_WINDOW,
  buildPriceSeries,
  dearestVendor,
  formatQty,
  isBelowReorder,
  marginFraction,
  movementInsights,
  parsePricePoints,
  priceInsights,
  productInsights,
  rankInsights,
  type StockPosition,
} from '@/lib/product-intelligence';
import { cn, formatCurrency } from '@/lib/utils';
import {
  DeltaLabel,
  InsightList,
  IntelCard,
  IntelEmpty,
  IntelError,
  MetricTile,
  SectionLabel,
  SectionSkeleton,
} from './intelligence-ui';

interface StockRow {
  locationId: string;
  locationName: string;
  qty: number | null;
  threshold: number | null;
}

interface Props {
  productId: string;
  position: StockPosition;
  /** Header location, or '' for all locations. */
  locationId: string;
  locationName: string;
  /** Every location the product is set up at that this user may see. */
  stockRows: StockRow[];
  isWarehouse: (locationId: string) => boolean;
  /** Jumps to the Pricing panel from the Best vendor card. */
  onSeePricing: () => void;
}

/**
 * Summary before detail: what an owner needs in the first two seconds — what's
 * wrong, what it's worth, and who to buy from.
 */
export function OverviewPanel({
  productId,
  position,
  locationId,
  locationName,
  stockRows,
  isWarehouse,
  onSeePricing,
}: Props) {
  // Both feeds are shared with the Pricing and Movement panels (same query
  // keys), so this reuses their cache rather than issuing new requests.
  const prices = useQuotedPriceHistory(productId);
  const movements = useProductStockMovements(productId, locationId || undefined);
  // Pinned once so derived windows don't drift between renders.
  const [now] = useState(() => new Date());

  const points = useMemo(() => parsePricePoints(prices.data?.data), [prices.data]);
  const series = useMemo(() => buildPriceSeries(points, INITIAL_WINDOW, now), [points, now]);

  // Each feed adds its rules only once it has resolved; one still loading
  // contributes nothing rather than holding back the product-level insights.
  const insights = rankInsights([
    ...productInsights(position),
    ...(points.length > 0 ? priceInsights(series) : []),
    ...(locationId ? movementInsights(movements.movements, position.qty, now) : []),
  ]);

  return (
    <div className="space-y-6">
      <InsightList insights={insights} />

      <section>
        <SectionLabel>
          Stock &amp; margin · {position.scope === 'location' ? locationName || 'this location' : 'all locations'}
        </SectionLabel>
        <MetricTiles position={position} />
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section>
          <SectionLabel>Stock by location</SectionLabel>
          <StockByLocation rows={stockRows} locationId={locationId} isWarehouse={isWarehouse} />
        </section>

        <section>
          <SectionLabel>Best vendor</SectionLabel>
          {prices.isLoading ? (
            <SectionSkeleton lines={2} />
          ) : prices.isError ? (
            <IntelError message="Vendor prices are unavailable." onRetry={() => prices.refetch()} />
          ) : points.length === 0 ? (
            <IntelEmpty
              icon={Store}
              title="No vendor price history yet"
              detail="Vendor prices appear here once a supplier price is recorded."
            />
          ) : series.vendors.length === 0 ? (
            <IntelEmpty icon={Store} title={`No price changes in the ${INITIAL_WINDOW.fullName}`} />
          ) : (
            <BestVendorCard series={series} onSeePricing={onSeePricing} />
          )}
        </section>
      </div>
    </div>
  );
}

function MetricTiles({ position: p }: { position: StockPosition }) {
  const margin = marginFraction(p.cost, p.sell);
  const varies = 'Varies';
  const money = (v: number | null, vary: boolean) => (vary ? varies : v === null ? '—' : formatCurrency(v));
  const notHere = p.scope === 'location' && !p.stocked;

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
      <MetricTile
        value={notHere ? 'Not stocked' : p.qty === null ? '—' : formatQty(p.qty)}
        caption={p.scope === 'location' ? 'on hand here' : 'on hand'}
        alert={p.qty !== null && p.qty <= 0 && p.stocked}
      />
      <MetricTile value={money(p.sell, p.sellVaries)} caption="selling" />
      <MetricTile
        value={p.stockValue === null ? '—' : formatCurrency(Math.round(p.stockValue)).replace(/\.00$/, '')}
        caption="stock value"
      />
      <MetricTile value={money(p.cost, p.costVaries)} caption="unit cost" />
      <MetricTile
        value={p.costVaries || p.sellVaries ? varies : margin === null ? '—' : `${(margin * 100).toFixed(1)}%`}
        caption="margin"
        alert={margin !== null && margin < 0.1}
      />
      <MetricTile
        value={p.threshold === null ? '—' : formatQty(p.threshold)}
        caption="reorder at"
        alert={isBelowReorder(p.qty, p.threshold)}
      />
    </div>
  );
}

function BestVendorCard({
  series,
  onSeePricing,
}: {
  series: ReturnType<typeof buildPriceSeries>;
  onSeePricing: () => void;
}) {
  const best = series.vendors[0];
  const dearest = dearestVendor(series);
  const saving =
    dearest && dearest.currentPrice > 0 ? (dearest.currentPrice - best.currentPrice) / dearest.currentPrice : null;
  return (
    <IntelCard>
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate text-base font-semibold text-foreground">{best.payeeName}</span>
        <span className="text-base font-semibold tabular-nums text-primary">{formatCurrency(best.currentPrice)}</span>
      </div>
      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="flex-1">
          {saving !== null && saving >= 0.01 && dearest
            ? `${Math.round(saving * 100)}% cheaper than ${dearest.payeeName}`
            : 'Only vendor on record'}
        </span>
        <DeltaLabel fraction={best.windowDeltaFraction} />
      </div>
      <button type="button" onClick={onSeePricing} className="mt-3 text-sm font-medium text-primary hover:underline">
        See price trends →
      </button>
    </IntelCard>
  );
}

/** The selected location beside every location, then one row per location —
 *  the cross-location picture the editable tabs leave out. */
function StockByLocation({
  rows,
  locationId,
  isWarehouse,
}: {
  rows: StockRow[];
  locationId: string;
  isWarehouse: (id: string) => boolean;
}) {
  if (rows.length === 0) {
    return <IntelEmpty icon={Store} title="Not stocked at any location" />;
  }
  const sorted = [...rows].sort((a, b) => {
    if (a.locationId === locationId) return -1;
    if (b.locationId === locationId) return 1;
    return a.locationName.localeCompare(b.locationName, undefined, { sensitivity: 'base' });
  });
  const total = rows.reduce((sum, r) => sum + (r.qty ?? 0), 0);
  const here = locationId ? rows.find((r) => r.locationId === locationId) : undefined;
  const hereLabel = locationId && isWarehouse(locationId) ? 'This warehouse' : 'This store';

  return (
    <IntelCard className="p-0">
      <div className="grid grid-cols-2 gap-4 px-4 py-3">
        {locationId && <Figure caption={hereLabel} value={here?.qty == null ? '—' : formatQty(here.qty)} />}
        <Figure
          caption={rows.length === 1 ? 'All locations' : `All ${rows.length} locations`}
          value={formatQty(total)}
        />
      </div>
      <ul className="space-y-1 border-t border-border p-2">
        {sorted.map((r) => (
          <LocationRow
            key={r.locationId}
            row={r}
            isHere={r.locationId === locationId}
            warehouse={isWarehouse(r.locationId)}
          />
        ))}
      </ul>
    </IntelCard>
  );
}

function Figure({ caption, value }: { caption: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{caption}</div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

function LocationRow({ row: r, isHere, warehouse }: { row: StockRow; isHere: boolean; warehouse: boolean }) {
  const { qty, threshold } = r;
  let status: { text: string; cls: string } | null = null;
  if (qty !== null && qty <= 0) status = { text: 'Out of stock', cls: 'text-red-600' };
  else if (isBelowReorder(qty, threshold))
    status = { text: `Low · reorder at ${formatQty(threshold!)}`, cls: 'text-amber-700' };
  else if (threshold !== null && threshold > 0)
    status = { text: `Reorder at ${formatQty(threshold)}`, cls: 'text-muted-foreground' };
  const Icon = warehouse ? Warehouse : Store;

  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-lg px-2.5 py-2',
        // Warehouses sit on an orange tint, as on the inventory cards, so a
        // warehouse never reads as a store.
        warehouse && 'border border-orange-200! bg-orange-50',
      )}
    >
      <Icon className={cn('h-4 w-4 shrink-0', warehouse ? 'text-orange-600' : 'text-primary')} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">
          {r.locationName || 'Unnamed location'}
          {isHere && (
            <span className={cn('ml-2 text-xs font-semibold', warehouse ? 'text-orange-700' : 'text-primary')}>
              {warehouse ? 'This warehouse' : 'This store'}
            </span>
          )}
        </div>
        {status && <div className={cn('text-xs font-medium', status.cls)}>{status.text}</div>}
      </div>
      {warehouse && (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
          <Warehouse className="h-3 w-3" aria-hidden="true" />
          Warehouse
        </span>
      )}
      <span className="w-14 shrink-0 text-right text-base font-semibold tabular-nums text-foreground">
        {qty === null ? '—' : formatQty(qty)}
      </span>
    </li>
  );
}
